// src/pages/PatientDetails.js
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  Box,
  Chip,
  Paper
} from '@mui/material';
import { getCodingFlowVisitDetails, deleteCodingApolloDiseaseDetection } from '../../api';
import useFetch from '../../hooks/useFetch';
import TaskModal from './TaskModal';
import { Button } from '@mui/material';

const formatApolloMessage = (message, handleDeleteMessage) => {
  const info = (
    <>
      <div
        style={{
          backgroundColor: '#A1E7A4', // Different pastel green for header
          padding: '8px 12px',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          color: '#333',
          position: 'relative', // For positioning the "X" in the top-right corner
          fontWeight: 'bold',
          textAlign: 'center',
        }}
      >
        Apollo Message ({message.DiagnosisCode}/{message.HCC})
        <span
          onClick={() => handleDeleteMessage(message)}
          style={{
            position: 'absolute',
            top: '4px',
            right: '8px',
            cursor: 'pointer',
            padding: '4px',
            color: '#555',
            borderRadius: '4px',
            fontWeight: 'bold',
            transition: 'background-color 0.3s',
          }}
          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#E57373')}
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          ✕
        </span>
      </div>
      <div style={{ padding: '12px 18px' }}>
        <div><strong>{message.created_by}:</strong> {message.Description}</div>
        <div
            style={{
              display: 'flex',
            }}
          >
            <div style={{ color: 'grey', marginLeft: 'auto', fontSize: '12px' }}>
              {new Date(message.created_at).toLocaleString()}
            </div>
          </div>
        {message.feedback_by && (
          <div
            style={{
              display: 'flex',
              marginTop: '12px',
              padding: '10px',
              backgroundColor: '#FFECB3', // Light yellow background for feedback section
              borderRadius: '8px',
              border: '1px solid #FFB74D',
            }}
          >
            <div><strong>{message.feedback_by}:</strong> {message.feedback}</div>
            <div style={{ color: 'grey', marginLeft: 'auto', fontSize: '12px' }}>
              {new Date(message.feedback_at).toLocaleString()}
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div>
      <Box
        sx={{
          backgroundColor: '#E5F1B5', // Pastel light green for main content
          borderRadius: '8px',
          display: 'inline-block',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          boxShadow: '2px 4px 8px rgba(0, 0, 0, 0.15)', // Soft shadow for depth
          fontFamily: 'Arial, sans-serif',
          color: '#333', // Dark text for readability
          overflow: 'hidden', // Ensures the border radius applies fully
          width: '400px', // Set width to 400px
          marginTop: '15px'
        }}
      >
        {info}
      </Box>
      <br />
    </div>
  );
};

const statusColors = {
  "Code Added": "#e8f5e9",
  "Code Accepted": "#f0faff",
  "Code Dismissed": "#ffecec",
  "Code Unattended": "#fff9e6",
  "Existing Code Refreshed": '#e0f7fa',
  "Existing Code UnRefreshed": "#f0faff",
  "No Code Added": "#e0e0e0",
  "Code Follow Up": "#ebe3f3",
  "Recapture Accepted": "#f0faff",
  "Recapture Unattended": "#fff9e6",
  "Recapture Dismissed": "#ffecec",
  "Recapture Follow Up": "#ebe3f3",
  "Allymar Accepted": "#f0faff",
  "Allymar Unattended": "#fff9e6",
  "Allymar Dismissed": "#ffecec",
  "Allymar Follow Up": "#ebe3f3",
};

const chipStyles = {
  default: { backgroundColor: '#333', color: '#fff' },
  warning: { backgroundColor: '#cc7000', color: '#fff' }
};

const VisitDetails = ({ username, token }) => {
  const [searchParams] = useSearchParams();
  const department = searchParams.get('department');
  const provider = searchParams.get('provider');
  const metric = searchParams.get('metric');
  const days = searchParams.get('days');

  const [params, setParams] = useState({ username, token, department, provider, metric, days });
  const { data, loading, error } = useFetch(getCodingFlowVisitDetails, params);

  const [openModal, setOpenModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [refresh, setRefresh] = useState(false);
  
  useEffect(() => {
    console.log(refresh);
    setParams((prev) => ({ ...prev, refresh }));
  }, [refresh]);

  const handleOpenModal = (action) => {
    setSelectedAction(action);
    setOpenModal(true);
  };
  
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedAction(null);
    handleRefresh();
  };

  const handleRefresh = () => {
    setRefresh((prev) => !prev);
  };

  const handleDeleteMessage = async (message) => {
    await deleteCodingApolloDiseaseDetection({
      username,
      token,
      patientId: message.patient_id
    }, 'Clinical_Leadership', message);
    handleRefresh();
  };

  if (loading) return <CircularProgress />;
  if (error) return <div>Error: {error.message}</div>;
  if (!data) return null;

  const grouped = data.reduce((acc, row) => {
    const formattedDate = row.encounter_date;
    const key = `${row.patient_id}-${formattedDate}`;
    if (!acc[key]) acc[key] = { patient_id: row.patient_id, encounter_date: formattedDate, actions: [] };
    acc[key].actions.push(row);
    return acc;
  }, {});

  const groupedArray = Object.values(grouped).map(visit => {
    const hasOtherActions = visit.actions.some(action => action.code_status !== 'No Code Added');
    const filteredActions = hasOtherActions
      ? visit.actions.filter(action => action.code_status !== 'No Code Added')
      : visit.actions;
    return { ...visit, actions: filteredActions };
  });

  return (
    <div>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        Visit Details – {department} / {provider} / {metric.replaceAll('_', ' ')}
      </Typography>
      <Grid container spacing={3}>
        {groupedArray.map((visit, index) => (
          <Grid item xs={12} key={index}>
            <Paper elevation={2} sx={{ p: 2, backgroundColor: '#e9e9e9' }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                🧍 Patient ID: <strong>{visit.patient_id}</strong>
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
                📅 Encounter Date: <strong>{visit.encounter_date}</strong>
              </Typography>

              <Grid container spacing={2}>
                {visit.actions.map((action, idx) => (
                  <Grid item xs={12} md={6} key={idx}>
                    <Card sx={{ backgroundColor: statusColors[action.code_status] || '#f0f0f0' }} variant="outlined">
                      <CardContent>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Chip
                            label={action.code_status}
                            sx={
                              action.code_status === 'Code Unattended' || action.code_status === 'Existing Code UnRefreshed' || action.code_status === 'Recapture Accepted' || action.code_status === 'Code Accepted' || action.code_status === 'Recapture Unattended' || action.code_status === 'Allymar Accepted' || action.code_status === 'Allymar Unattended'
                                ? chipStyles.warning
                                : chipStyles.default
                            }
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{
                              color: '#555',
                              borderColor: '#ccc',
                              textTransform: 'none',
                              fontWeight: 400,
                              '&:hover': {
                                borderColor: '#999',
                                backgroundColor: '#f5f5f5'
                              }
                            }}
                            onClick={() => handleOpenModal(action)}
                          >
                            + Feedback
                          </Button>
                        </Box>
                        <Typography variant="body2">
                          <strong>Code Source:</strong> {action.code_source}
                        </Typography>
                        <Typography variant="body2">
                          <strong>HCC Code:</strong> {action.hcc_code || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Diagnosis Code:</strong> {action.diagnosis_code || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Diagnosis Desc:</strong> {action.diagnosis_description || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Last Assessed:</strong> {action.details.recent_dt || 'Never'}
                        </Typography>
                        {action.messages.map((msg) => (
                            formatApolloMessage(msg, handleDeleteMessage)
                          ))
                        }
                        {Array.isArray(action.details) && action.details.length > 0 && (
                          <div>
                          <Typography variant="body2">
                            <strong>Coder Communications</strong>
                          </Typography>
                          <Box sx={{ mt: 1, pl: 1, borderLeft: '3px solid #aaa' }}>
                            {action.details.map((detail, dIdx) => (
                              <Box key={dIdx} sx={{ mb: 1 }}>
                                <Typography variant="caption" display="block">
                                  <strong>Detected At:</strong> {detail.detected_at}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  <strong>Detected By:</strong> {detail.detected_by}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  <strong>Message:</strong> {detail.message_content}
                                </Typography>
                                {detail.status && (
                                  <Typography variant="caption" display="block">
                                    <strong>Status:</strong> {detail.status}
                                  </Typography>
                                )}
                                {detail.message_type && (
                                  <Typography variant="caption" display="block">
                                    <strong>Channel:</strong> {detail.message_type}
                                  </Typography>
                                )}
                                {detail.feedback && (
                                  <Typography variant="caption" display="block">
                                    <strong>{detail.feedback_by} ({detail.feedback_at}):</strong> {detail.feedback}
                                  </Typography>
                                )}
                                <Divider sx={{ mt: 1 }} />
                              </Box>
                            ))}
                          </Box>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>
      {openModal && (
        <TaskModal
          open={openModal}
          onClose={handleCloseModal}
          username={username}
          token={token}
          action={selectedAction}
        />
      )}
    </div>
  );
};

export default VisitDetails;
